import {
  DEFAULT_SETTINGS,
  INITIAL_MOCK_GAME_STATE,
  type GameLogMessage,
  type GamePlayer,
  type HandCard,
  type MockGameAction,
  type MockGameState,
  type Submission,
} from './mockGameTypes';
import { countBlanks } from '@deck-after-dark/shared';
import { toggleCardSelection, clearCardSelection, writeInsForSelection } from './cardSelection';
import {
  DEMO_BLACK_CARDS,
  DEMO_BOT_ANSWERS,
  DEMO_BOTS,
  DEMO_WHITE_CARDS,
  PLAYER_GRADIENTS,
} from './mockCardPool';

let idCounter = 0;
function nextId(prefix: string) {
  idCounter += 1;
  return `${prefix}-${idCounter}`;
}

function getInitials(name: string) {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0]![0]! + parts[1]![0]!).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

function generateRoomCode() {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let code = '';
  for (let i = 0; i < 4; i += 1) {
    code += chars[Math.floor(Math.random() * chars.length)]!;
  }
  return code;
}

function shuffle<T>(arr: T[]): T[] {
  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j]!, copy[i]!];
  }
  return copy;
}

function makePlayer(
  name: string,
  opts: Partial<GamePlayer> & { gradient?: string } = {},
): GamePlayer {
  const gradient = opts.gradient ?? PLAYER_GRADIENTS[0]!;
  return {
    id: opts.id ?? nextId('player'),
    name,
    initials: getInitials(name),
    score: opts.score ?? 0,
    isHost: opts.isHost ?? false,
    isReady: opts.isReady ?? false,
    isConnected: opts.isConnected ?? true,
    gradient,
    ...opts,
  };
}

function addLog(state: MockGameState, msg: Omit<GameLogMessage, 'id'>): GameLogMessage[] {
  return [...state.log, { ...msg, id: nextId('log') }];
}

function dealHand(includeWild: boolean): HandCard[] {
  const picked = shuffle(DEMO_WHITE_CARDS).slice(0, 5);
  const cards: HandCard[] = picked.map((text) => ({
    id: nextId('card'),
    text,
    type: 'white',
  }));
  if (includeWild) {
    cards.push({ id: 'wild', text: '', type: 'wild' });
  }
  return cards;
}

function pickBlackCard(roundNumber: number): string {
  return DEMO_BLACK_CARDS[(roundNumber - 1) % DEMO_BLACK_CARDS.length]!;
}

function rotateJudge(
  players: GamePlayer[],
  currentJudgeId: string | null,
  excludeId?: string | null,
): string {
  // In the single-player mock demo the human (excludeId) plays every round and
  // also resolves judging, so the judge always rotates among the bots. This keeps
  // the offline demo fully playable (the human is never locked into a judge-only
  // round). Live multiplayer judging is server-authoritative and unaffected.
  const candidates = players.filter((p) => p.isConnected && p.id !== excludeId);
  const pool = candidates.length > 0 ? candidates : players.filter((p) => p.isConnected);
  if (pool.length === 0) return players[0]?.id ?? '';
  const idx = pool.findIndex((p) => p.id === currentJudgeId);
  const next = pool[(idx + 1) % pool.length];
  return next?.id ?? pool[0]!.id;
}

function buildBotSubmissions(players: GamePlayer[], currentPlayerId: string): Submission[] {
  return players
    .filter((p) => p.id !== currentPlayerId && p.isConnected)
    .map((p, i) => ({
      id: nextId('sub'),
      playerId: p.id,
      text: DEMO_BOT_ANSWERS[i % DEMO_BOT_ANSWERS.length]!,
    }));
}

function createRoomState(
  roomName: string,
  nickname: string,
  settings: Partial<typeof DEFAULT_SETTINGS>,
): MockGameState {
  const host = makePlayer(nickname || 'Host', {
    isHost: true,
    isReady: true,
    gradient: PLAYER_GRADIENTS[0],
  });
  const bots = DEMO_BOTS.map((b) => makePlayer(b.name, { isReady: true, gradient: b.gradient }));
  const roomCode = generateRoomCode();
  const mergedSettings = { ...DEFAULT_SETTINGS, ...settings, roomName: roomName || DEFAULT_SETTINGS.roomName };

  return {
    ...INITIAL_MOCK_GAME_STATE,
    roomCode,
    currentPlayerId: host.id,
    players: [host, ...bots],
    // The human (host) is always a player in the solo demo; a bot holds the judge
    // seat so the human can submit, then resolves judging via canPickWinner below.
    judgeId: bots[0]?.id ?? host.id,
    settings: mergedSettings,
    phase: 'lobby',
    log: [
      { id: nextId('log'), author: host.name, text: 'Ready to get roasted?' },
      { id: nextId('log'), system: true, text: `${bots[0]?.name} joined the room` },
    ],
  };
}

function joinRoomState(state: MockGameState, roomCode: string, nickname: string): MockGameState {
  const code = roomCode.toUpperCase().trim();
  const name = nickname.trim() || 'Guest';

  if (state.roomCode && state.roomCode !== code) {
    return { ...state, joinError: 'Room code not found. Check the code and try again.' };
  }

  if (state.roomCode === code && state.players.some((p) => p.name === name)) {
    return { ...state, joinError: null, phase: 'lobby' };
  }

  const guest = makePlayer(name, {
    gradient: PLAYER_GRADIENTS[3],
    isReady: true,
  });

  if (state.roomCode === code) {
    return {
      ...state,
      joinError: null,
      currentPlayerId: guest.id,
      players: [...state.players, guest],
      phase: 'lobby',
      log: addLog(state, { system: true, text: `${name} joined the room` }),
    };
  }

  const host = makePlayer('NightOwl', { isHost: true, isReady: true, gradient: PLAYER_GRADIENTS[0] });
  const bots = DEMO_BOTS.map((b) => makePlayer(b.name, { isReady: true, gradient: b.gradient }));

  return {
    ...INITIAL_MOCK_GAME_STATE,
    roomCode: code,
    currentPlayerId: guest.id,
    players: [host, ...bots, guest],
    judgeId: host.id,
    phase: 'lobby',
    log: [{ id: nextId('log'), system: true, text: `${name} joined the room` }],
  };
}

function startRound(state: MockGameState): MockGameState {
  const roundNumber = state.roundNumber + 1;
  const blackCardText = pickBlackCard(roundNumber);
  const blankCount = countBlanks(blackCardText);
  const blackCardPick = blankCount > 0 ? Math.min(3, blankCount) : 1;
  const hand = dealHand(state.settings.wildCardsEnabled);

  return {
    ...state,
    phase: 'playing',
    roundNumber,
    blackCardText,
    blackCardPick,
    hand,
    selectedCardIds: [],
    selectedCardId: null,
    writeInText: '',
    submissions: [],
    lastRoundResult: null,
    canPickWinner: false,
    log: addLog(state, { system: true, text: `Round ${roundNumber} started` }),
  };
}

export function mockGameReducer(state: MockGameState, action: MockGameAction): MockGameState {
  switch (action.type) {
    case 'CREATE_ROOM':
      return createRoomState(action.payload.roomName, action.payload.nickname, action.payload.settings);

    case 'JOIN_ROOM': {
      const code = action.payload.roomCode.replace(/\s/g, '').toUpperCase();
      if (code.length < 4) {
        return { ...state, joinError: 'Enter a 4-character room code.' };
      }
      if (!action.payload.nickname.trim()) {
        return { ...state, joinError: 'Enter a nickname to join.' };
      }
      return joinRoomState(state, code, action.payload.nickname);
    }

    case 'CLEAR_JOIN_ERROR':
      return { ...state, joinError: null };

    case 'TOGGLE_READY': {
      if (!state.currentPlayerId) return state;
      return {
        ...state,
        players: state.players.map((p) =>
          p.id === state.currentPlayerId ? { ...p, isReady: !p.isReady } : p,
        ),
      };
    }

    case 'START_GAME':
      return startRound(state);

    case 'TOGGLE_CARD': {
      const nextIds = toggleCardSelection(state.selectedCardIds, action.payload, state.blackCardPick);
      return {
        ...state,
        selectedCardIds: nextIds,
        selectedCardId: nextIds[0] ?? null,
      };
    }

    case 'CLEAR_SELECTION':
      return { ...state, selectedCardIds: clearCardSelection(), selectedCardId: null };

    case 'SELECT_CARD': {
      if (action.payload === null) {
        return { ...state, selectedCardIds: clearCardSelection(), selectedCardId: null };
      }
      const nextFromSelect = toggleCardSelection(
        state.selectedCardIds,
        action.payload,
        state.blackCardPick,
      );
      return {
        ...state,
        selectedCardIds: nextFromSelect,
        selectedCardId: nextFromSelect[0] ?? null,
      };
    }

    case 'SET_WRITE_IN':
      return { ...state, writeInText: action.payload.slice(0, 60) };

    case 'CONFIRM_WRITE_IN': {
      const trimmed = state.writeInText.trim();
      if (!trimmed) return state;
      return { ...state, writeInText: trimmed };
    }

    case 'SUBMIT_ANSWER': {
      if (!state.currentPlayerId || state.selectedCardIds.length !== state.blackCardPick) return state;

      const wilds = writeInsForSelection(state.selectedCardIds, state.hand, state.writeInText);
      const wildCount = state.selectedCardIds.filter(
        (id) => state.hand.find((c) => c.id === id)?.type === 'wild',
      ).length;
      if (wildCount > 0 && wilds.length !== wildCount) return state;

      const answerParts = state.selectedCardIds.map((id) => {
        const card = state.hand.find((c) => c.id === id);
        if (!card) return '';
        if (card.type === 'wild') return state.writeInText.trim();
        return card.text;
      });
      const answerText = answerParts.filter(Boolean).join(' / ');
      if (!answerText) return state;

      const playerSubmission: Submission = {
        id: nextId('sub'),
        playerId: state.currentPlayerId,
        text: answerText,
      };

      const botSubs = buildBotSubmissions(state.players, state.currentPlayerId);
      const currentPlayer = state.players.find((p) => p.id === state.currentPlayerId);

      return {
        ...state,
        phase: 'judging',
        submissions: [playerSubmission, ...botSubs],
        // Solo demo: the human resolves judging even though a bot holds the seat.
        canPickWinner: true,
        log: addLog(state, {
          author: currentPlayer?.name ?? 'You',
          text: 'played a card.',
          self: true,
        }),
      };
    }

    case 'PICK_WINNER': {
      const winner = state.players.find((p) => p.id === action.payload);
      const winningSub = state.submissions.find((s) => s.playerId === action.payload);
      if (!winner || !winningSub || !state.blackCardText) return state;

      const updatedPlayers = state.players.map((p) =>
        p.id === winner.id ? { ...p, score: p.score + 1 } : p,
      );
      const updatedWinner = updatedPlayers.find((p) => p.id === winner.id)!;
      const gameOver = updatedWinner.score >= state.settings.pointsToWin;

      return {
        ...state,
        players: updatedPlayers,
        phase: gameOver ? 'gameOver' : 'roundResult',
        lastRoundResult: {
          winnerId: winner.id,
          answerText: winningSub.text,
          promptText: state.blackCardText,
        },
        roundHistory: [
          ...state.roundHistory,
          {
            roundNumber: state.roundNumber,
            promptText: state.blackCardText,
            answerText: winningSub.text,
            winnerId: winner.id,
            winnerName: winner.name,
          },
        ],
        log: addLog(state, { system: true, text: `${winner.name} won the round!` }),
      };
    }

    case 'NEXT_ROUND': {
      if (state.phase === 'gameOver') return state;
      const nextJudge = rotateJudge(state.players, state.judgeId, state.currentPlayerId);
      return startRound({ ...state, judgeId: nextJudge });
    }

    case 'PLAY_AGAIN': {
      if (!state.roomCode) return INITIAL_MOCK_GAME_STATE;
      return {
        ...state,
        phase: 'lobby',
        roundNumber: 0,
        players: state.players.map((p) => ({ ...p, score: 0, isReady: p.isHost })),
        hand: [],
        selectedCardIds: [],
        selectedCardId: null,
        writeInText: '',
        submissions: [],
        lastRoundResult: null,
        roundHistory: [],
        blackCardText: null,
        blackCardPick: 1,
        judgeId: state.players.find((p) => p.isHost)?.id ?? state.players[0]?.id ?? null,
      };
    }

    case 'NEW_ROOM':
    case 'RESET':
      return { ...INITIAL_MOCK_GAME_STATE };

    default:
      return state;
  }
}

export function getCurrentPlayer(state: MockGameState): GamePlayer | undefined {
  return state.players.find((p) => p.id === state.currentPlayerId);
}

export function getJudge(state: MockGameState): GamePlayer | undefined {
  return state.players.find((p) => p.id === state.judgeId);
}

export function getWinner(state: MockGameState): GamePlayer | undefined {
  if (!state.lastRoundResult) return undefined;
  return state.players.find((p) => p.id === state.lastRoundResult!.winnerId);
}

export function getSortedPlayers(state: MockGameState): GamePlayer[] {
  return [...state.players].sort((a, b) => b.score - a.score);
}

export function playerToScoreboard(p: GamePlayer, judgeId: string | null) {
  return {
    id: p.id,
    name: p.name,
    initials: p.initials,
    score: p.score,
    isHost: p.isHost,
    isJudge: p.id === judgeId,
    isReady: p.isReady,
    isConnected: p.isConnected,
    gradient: p.gradient,
  };
}
